import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import TaskUtils from 'services/TaskUtils';
import { Shift } from 'generated/api';

export interface AwayInfo {
  away: boolean;
  reason?: string;
}

const NOT_AWAY: AwayInfo = { away: false };

/**
 * Advisory OOO check for a task assignee. Fetches the assignee's non-working
 * shifts via getUserNonWorkingShifts — which is keyed on user_id (so it is
 * correct across clinics, unlike the clinic-scoped getDayShifts) and already
 * server-filtered to approved non-working — and reports whether any of them
 * cover today. Read-only: a miss simply shows no warning, and errors resolve to
 * "not away" with no toast (the thunk is dispatched without error options).
 *
 * Successful results are cached per user_id for the life of the hook so toggling
 * the assignee back and forth does not refetch (a failed lookup is not cached,
 * so it is retried), and in-flight responses are guarded against a fast assignee
 * change so a stale reply can't paint the wrong warning.
 */
export default function useAssigneeAway(userId?: string): AwayInfo {
  const dispatch = useAppDispatch();
  const cache = useRef<Record<string, AwayInfo>>({});
  const [, setSettled] = useState(0);

  // Derived during render, not held in state: state written from a passive
  // effect would keep painting the previous assignee's verdict under the new
  // assignee's name for a frame after the assignee changes.
  const info = userId && userId !== 'none' ? cache.current[userId] || NOT_AWAY : NOT_AWAY;

  useEffect(() => {
    if (!userId || userId === 'none' || cache.current[userId]) return undefined;

    let cancelled = false;

    dispatch(api.Shifts.getUserNonWorkingShifts({ userId })).then((shifts?: Shift[]) => {
      // The unwrapped dispatch resolves to undefined on any non-2xx, which is
      // not the same as "no absences" — leave it uncached so it can be retried.
      if (!shifts) return;
      const awayShift = TaskUtils.getAwayShiftOn(shifts, moment().unix());
      // Cache even a superseded reply: it is still the right answer for that
      // user, and dropping it defeats the cache on rapid assignee toggling.
      cache.current[userId] = awayShift
        ? { away: true, reason: awayShift.non_working_reason }
        : NOT_AWAY;
      if (cancelled) return;
      setSettled((count) => count + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, dispatch]);

  return info;
}
